import { useAuth } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import DailyLimitReached from "components/DailyLimitReached";
import SignIn from "components/SignIn";
import StreamingError from "components/StreamingError";
import OutOfCredits from "features/Subscription/components/OutOfCredits";
import { ASK_PAPER_CREDITS_PER_MONTH_FREE_USER } from "constants/config";
import useCredits from "features/User/hooks/useCredits";
import {
  AskPaperThreadMessage as AskPaperThreadMessageType,
  AskPaperThreadRole,
} from "helpers/api";
import pluralize from "helpers/pluralize";
import useAnalytics from "hooks/useAnalytics";
import useAskPaperThread from "hooks/useAskPaperThread";
import useLabels from "hooks/useLabels";
import { useStreamingForAskPaper } from "hooks/useStreaming";
import { useCallback, useEffect, useRef, useState } from "react";
import { CreditedFeature } from "store/slices/subscription";
import AskPaperEmptyThread from "./AskPaperEmptyThread";
import AskPaperMessageLoading from "./AskPaperMessageLoading";
import AskPaperPdfLoading from "./AskPaperPdfLoading";
import AskPaperThreadMessage from "./AskPaperThreadMessage";
import AskPaperUploadNewPaperGuide from "./AskPaperUploadNewPaperGuide";

type AskPaperThreadProps = {
  isPdfError: boolean;
  isPdfLoading: boolean;
  onClickUpgradeToPremium: () => void;
  onClose: () => void;
  onSelectExcerpt?: (text: string) => void;
  paperId: string;
};

function AskPaperThread({
  isPdfError,
  isPdfLoading,
  onClickUpgradeToPremium,
  onClose,
  onSelectExcerpt,
  paperId,
}: AskPaperThreadProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const notSignedIn = isLoaded && isSignedIn === false;

  const { askPaperChatEvent } = useAnalytics();

  const queryClient = useQueryClient();
  const [askPaperLabels, creditsLabels] = useLabels(
    "ask-paper",
    "tooltips.credits"
  );

  const [currentUserMessage, setCurrentUserMessage] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");

  const {
    isOutOfAskPaperCredits,
    askPaperCreditsRemaining,
    creditRefreshDate,
    isPremium,
    spendCreditsOnAskPaper,
  } = useCredits();

  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for the scrolling container

  const {
    data,
    isLoading: isThreadLoading,
    isError: isThreadError,
  } = useAskPaperThread(paperId);

  const handleNewUserMessage = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inputValue.length === 0) return;
    setCurrentUserMessage(inputValue);
    askPaperChatEvent(inputValue, paperId);
    setInputValue(""); // Clear input after submitting
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleExampleMessageClicked = (message: string, paperId: string) => {
    if (inputDisabled || isThreadError || data === undefined) return;
    setCurrentUserMessage(message);
    askPaperChatEvent(message, paperId);
  };

  const onSuccess = useCallback(() => {
    if (data === undefined)
      throw new Error("Error spending credits: Thread is not loaded");

    spendCreditsOnAskPaper(paperId, data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, paperId]);

  const {
    content: currentAssistantResponse,
    isAuthError,
    isDailyLimitReached,
    error: streamingError,
    isError: isStreamingError,
    isLoading: isMessageLoading,
    isComplete,
    isStreaming,
  } = useStreamingForAskPaper({
    enabled: !isThreadLoading && !!currentUserMessage && !notSignedIn,
    message: currentUserMessage,
    paperId: paperId,
    onSuccess: onSuccess,
  });

  useEffect(() => {
    if (!isComplete) return;

    // Now that the stream has concluded, save the current active messages onto the thread and reset.
    const previousMessages =
      (queryClient.getQueryData([
        "ask-paper",
        paperId,
      ]) as AskPaperThreadMessageType[]) || [];

    const newUserMessage: AskPaperThreadMessageType = {
      role: AskPaperThreadRole.USER,
      content: currentUserMessage!,
      created_at: new Date(Date.now() - 1000).toString(),
    };

    const newAssistantMessage: AskPaperThreadMessageType = {
      role: AskPaperThreadRole.ASSISTANT,
      content: currentAssistantResponse,
      created_at: Date().toString(),
    };

    // Update the cache by appending new messages
    queryClient.setQueryData(
      ["ask-paper", paperId],
      [...previousMessages, newUserMessage, newAssistantMessage]
    );

    setCurrentUserMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, paperId, queryClient]);

  // Scroll to the bottom of the messages container whenever the data changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [
    data,
    currentUserMessage,
    currentAssistantResponse,
    isThreadLoading,
    isPdfLoading,
    isMessageLoading,
  ]);

  const showIsOutOfCredits = isOutOfAskPaperCredits && !isPremium;

  const inputDisabled =
    isPdfLoading ||
    isPdfError ||
    isThreadLoading ||
    isAuthError ||
    showIsOutOfCredits ||
    isDailyLimitReached ||
    isStreaming;
  const showEmptyThread =
    data?.length === 0 && !currentUserMessage && !showIsOutOfCredits;

  return (
    <div className="flex overflow-hidden relative flex-col h-full">
      {isPdfLoading ? (
        <div className="p-4 h-full">
          <AskPaperPdfLoading />
        </div>
      ) : isPdfError ? (
        <div className="p-4 h-full">
          <AskPaperUploadNewPaperGuide onClose={onClose} />
        </div>
      ) : isThreadLoading && !notSignedIn ? (
        <div className="p-4 h-full">
          <AskPaperMessageLoading />
        </div>
      ) : isThreadError ? (
        <div className="p-4 h-full">
          <StreamingError />
        </div>
      ) : showEmptyThread ? (
        <div className="px-4 pt-4 h-full">
          <AskPaperEmptyThread
            onExampleMessageClicked={(message) =>
              handleExampleMessageClicked(message, paperId)
            }
          />
        </div>
      ) : (
        <div
          className="flex overflow-auto flex-col flex-1 gap-2 px-4 pt-4"
          ref={messagesEndRef}
        >
          {data?.map((message) => (
            <AskPaperThreadMessage
              key={message.created_at}
              message={message}
              onSelectExcerpt={onSelectExcerpt}
            />
          ))}
          {currentUserMessage && (
            <AskPaperThreadMessage
              key={currentUserMessage}
              message={{
                role: AskPaperThreadRole.USER,
                content: currentUserMessage,
                created_at: Date.now().toString(),
              }}
            />
          )}
          {isMessageLoading && currentAssistantResponse.length === 0 && (
            <AskPaperMessageLoading />
          )}
          {currentAssistantResponse && isStreaming && (
            <AskPaperThreadMessage
              key={currentAssistantResponse}
              message={{
                role: AskPaperThreadRole.ASSISTANT,
                content: currentAssistantResponse,
                created_at: Date.now().toString(),
              }}
              onSelectExcerpt={onSelectExcerpt}
            />
          )}
          {isStreamingError && (
            <div className="mt-5">
              <StreamingError error={streamingError} />
            </div>
          )}
          {isDailyLimitReached && (
            <div className="mt-5">
              <DailyLimitReached
                title={askPaperLabels["daily-limit-reached-error-title"]}
                description={askPaperLabels["daily-limit-reached-error-desc"]}
                showSupportEmail
              />
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          "relative bottom-0 px-4 pt-3 w-full bg-bg-base",
          isPremium ? "pb-6" : "pb-3"
        )}
      >
        {isAuthError || notSignedIn ? (
          <div className="p-4 h-full">
            <SignIn
              feature={CreditedFeature.ASK_PAPER}
              containerClassName="paywall"
            />
          </div>
        ) : showIsOutOfCredits ? (
          <div className="mt-5">
            <OutOfCredits
              feature={CreditedFeature.ASK_PAPER}
              containerClassName="paywall"
            />
          </div>
        ) : (
          <>
            <form
              className="flex relative bottom-0 w-full"
              onSubmit={handleNewUserMessage}
            >
              <input
                className="w-full h-[52px] pl-5 py-1.5 pr-12 border rounded-full"
                disabled={inputDisabled}
                name="message"
                placeholder={askPaperLabels["ask-this-paper-placeholder"]}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
              />
              <CoButton
                aria-label="Submit"
                className="!absolute right-1.5 top-1.5"
                disabled={inputDisabled || inputValue.length === 0}
                icon="icon-arrow-right"
                size="sm"
                type="submit"
                variant={ButtonVariant.Primary}
              />
            </form>
            {!isPremium && (
              <p className="mt-2 text-center tiny-normal text-fg-muted">
                {pluralize(creditsLabels["messages-left"], {
                  CREDITS_REMAINING: askPaperCreditsRemaining,
                  MAX_CREDIT: ASK_PAPER_CREDITS_PER_MONTH_FREE_USER,
                  RENEW_DATE: creditRefreshDate,
                })}
                <button
                  className="ml-1 underline"
                  onClick={onClickUpgradeToPremium}
                >
                  {creditsLabels["upgrade-to-premium"]}
                </button>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AskPaperThread;
